<script setup lang="ts">
/**
 * Die Frage nach der Messung — dieselbe, die easyrechnung.at stellt.
 *
 * Wortlaut, Nachweis und Ablage kommen aus `@eua/shared/consent`, das auch
 * `apps/site` liest: eine Frage, ein Cookie, eine Entscheidung für beide Hosts.
 * Why the wording looks the way it does — both answers as buttons of equal
 * weight on the first layer, the object named, nothing pre-selected, the way
 * back in the text — is written down there, next to the text it produced.
 *
 * Ein Dialog, wie auf der Website, und nicht mehr eine Leiste am Fuß. Two
 * surfaces asking the same question in two shapes are two questions to whoever
 * sees both. It is asked far more rarely now: most customers arrive from
 * easyrechnung.at, where they have already answered, and the shared record
 * means nothing is asked here at all.
 */
import { onMounted, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { CONSENT_NOTICE } from '@eua/shared'
import { useConsent } from '@/composables/useConsent'
import { Button } from '@/ui'

const { state, hydrate, accept, decline } = useConsent()

/*
 * Nothing renders before the stored decision has been read. The first paint
 * cannot know it, so a dialog in the initial markup would flash at every
 * customer who has long decided.
 */
const checked = ref(false)
const show = ref(false)

/*
 * A modal takes the page's focus order with it, so it has to manage focus
 * itself: focus moves in when it opens, and Tab cycles inside. There is no
 * close-without-deciding — Escape or a click on the backdrop would store
 * nothing and bring the dialog straight back, which is worse than saying so
 * with its absence.
 */
const dialog = ref<HTMLElement | null>(null)

onMounted(() => {
  hydrate()
  checked.value = true
})

watch(
  [checked, state],
  async () => {
    show.value = checked.value && state.value === 'unknown'
    document.documentElement.style.overflow = show.value ? 'hidden' : ''
    if (!show.value) return
    await Promise.resolve()
    dialog.value?.focus()
  },
  { immediate: true },
)

function trapFocus(event: KeyboardEvent): void {
  if (event.key !== 'Tab' || !dialog.value) return
  const targets = dialog.value.querySelectorAll<HTMLElement>('a[href], button')
  const first = targets[0]
  const last = targets[targets.length - 1]
  if (!first || !last) return
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first.focus()
  }
}
</script>

<template>
  <div
    v-if="show"
    class="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-brand-950/60 p-4 sm:p-6"
    @keydown="trapFocus"
  >
    <div
      ref="dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="consent-titel"
      aria-describedby="consent-text"
      tabindex="-1"
      class="w-full max-w-xl rounded-xl border border-line-strong bg-surface p-6 shadow-pop outline-none sm:p-8"
    >
      <h2
        id="consent-titel"
        class="text-lg font-semibold text-ink"
      >
        {{ CONSENT_NOTICE.titel }}
      </h2>

      <p
        id="consent-text"
        class="mt-3 text-sm text-muted"
      >
        {{ CONSENT_NOTICE.vorLink
        }}<RouterLink
          to="/datenschutzerklaerung"
          class="underline underline-offset-2"
          >{{ CONSENT_NOTICE.linkText }}</RouterLink
        >{{ CONSENT_NOTICE.nachLink }}
      </p>

      <!--
        The same variant on both: a pale refuse button next to a filled accept
        button reads as steering, and steering costs the consent.
      -->
      <div class="mt-6 flex flex-col gap-3 sm:flex-row">
        <Button
          variant="secondary"
          block
          @click="decline"
        >
          {{ CONSENT_NOTICE.ablehnen }}
        </Button>
        <Button
          variant="secondary"
          block
          @click="accept"
        >
          {{ CONSENT_NOTICE.erlauben }}
        </Button>
      </div>

      <p class="mt-4 text-2xs text-muted">{{ CONSENT_NOTICE.hinweis }}</p>
    </div>
  </div>
</template>
